/**
 * SHOPPING INSIGHTS — WIREFRAMES. THROWAWAY, ADMIN ONLY, SAMPLE DATA.
 *
 * This file exists so the owner can approve how the grocer's Insights LOOKS
 * before anything is built. It is deliberately disposable:
 *   · no backend, no API call, no real household — every number is invented
 *     here and badged SAMPLE;
 *   · its own EN/FR copy lives in this file, NOT in the locale files, so the
 *     shipped translations stay clean when this page is deleted;
 *   · it reuses the SAME shells, palette and chart library as the Review
 *     Shield stats page (ui/card, ui/badge, ui/select, ui/table, recharts and
 *     the COLORS map from components/ReviewShieldAnalytics.jsx), so what you
 *     see is what the finished screens would look like.
 *
 * Seven screens: Home · Customer lookup · Client purchases (9 tabs) · Explore
 * · Places · Pattern finder · Executive Summary.
 *
 * DELETE THIS FILE AND ITS ROUTE when phase 4A ships for real.
 */
import { useState } from 'react';
import {
    LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
    XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import {
    Search, Home, Users, Compass, MapPin, Sparkles, FileText, Sun, Moon,
    ShoppingBasket, AlertTriangle, TrendingUp, Clock, Filter, X, Download,
    ChevronRight, Building2, Lock, PieChart as PieIcon,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { useAuth } from '../lib/auth';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';

/* The palette is the Review Shield one, not a new one. */
const C = {
    emerald: '#10b981', amber: '#f59e0b', red: '#ef4444', blue: '#3b82f6',
    purple: '#8b5cf6', orange: '#f97316', slate: '#64748b', teal: '#14b8a6',
};
/* ONE COLOUR PER AISLE, the same on every chart and every screen. */
const AISLE = {
    produce: C.emerald, dairy: C.blue, meat: C.red, fish: C.teal,
    bakery: C.orange, pantry: C.amber, cleaning: C.purple, other: C.slate,
};

const T = {
    en: {
        wire: 'WIREFRAMES · SAMPLE DATA · nothing is real',
        back: 'Command Center', title: 'Shopping Insights', period: 'Last 30 days',
        nav: { home: 'Home', lookup: 'Customer lookup', profile: 'Client purchases',
            explore: 'Explore', places: 'Places', patterns: 'Pattern finder',
            summary: 'Executive Summary' },
        kpi: { orders: 'Orders', households: 'Households', late: 'Late or cancelled',
            aisle: 'Top aisle' },
        trend: 'Orders over time', share: 'Share by aisle', units: 'of units',
        noticed: 'What we noticed', seeAll: 'See all (12)',
        searchPh: 'Card number, name, phone or address',
        more: 'More filters', clear: 'Clear all', export: 'Export',
        results: 'results', open: 'Open',
        reason: 'Why are you opening this household?', reasonPh: 'Support call about a late order',
        tabs: ['Overview', 'Cart now', 'Cart history', 'Spending', 'Habits',
            'Deals and points', 'Service', 'Notes and actions', 'Access log'],
        noPrices: 'No price list yet — everything here is counts and units.',
        placesTitle: 'Buildings, postal codes and cities',
        index: 'Compared with the city average', city: 'City average',
        base: 'Based on', orders: 'orders', households: 'households',
        scope: 'Whole store', pdf: 'Download PDF', weekly: 'Email me this every Monday',
        computed: 'Every number here is computed in code. Wording only is assisted.',
        findings: 'Top findings', opps: 'Opportunities', actions: 'Suggested actions',
        masked: 'Masked for your role',
    },
    fr: {
        wire: 'MAQUETTES · DONNÉES EXEMPLE · rien n\'est réel',
        back: 'Centre de commande', title: 'Aperçu des achats', period: '30 derniers jours',
        nav: { home: 'Accueil', lookup: 'Recherche de client', profile: 'Achats du client',
            explore: 'Explorer', places: 'Lieux', patterns: 'Tendances',
            summary: 'Résumé de direction' },
        kpi: { orders: 'Commandes', households: 'Foyers', late: 'En retard ou annulées',
            aisle: 'Rayon principal' },
        trend: 'Commandes dans le temps', share: 'Part par rayon', units: 'des articles',
        noticed: 'Ce qu\'on a remarqué', seeAll: 'Tout voir (12)',
        searchPh: 'Numéro de carte, nom, téléphone ou adresse',
        more: 'Plus de filtres', clear: 'Tout effacer', export: 'Exporter',
        results: 'résultats', open: 'Ouvrir',
        reason: 'Pourquoi ouvrez-vous ce foyer?', reasonPh: 'Appel au sujet d\'une livraison en retard',
        tabs: ['Vue d\'ensemble', 'Panier en cours', 'Historique', 'Dépenses', 'Habitudes',
            'Offres et points', 'Service', 'Notes et actions', 'Journal d\'accès'],
        noPrices: 'Pas encore de liste de prix — tout ici est en nombres et articles.',
        placesTitle: 'Immeubles, codes postaux et villes',
        index: 'Par rapport à la moyenne de la ville', city: 'Moyenne de la ville',
        base: 'Basé sur', orders: 'commandes', households: 'foyers',
        scope: 'Tout le magasin', pdf: 'Télécharger le PDF', weekly: 'Envoyez-moi ceci chaque lundi',
        computed: 'Chaque chiffre est calculé dans le code. Seule la formulation est assistée.',
        findings: 'Constats principaux', opps: 'Occasions', actions: 'Actions suggérées',
        masked: 'Masqué pour votre rôle',
    },
};

/* ── invented data, all of it ─────────────────────────────────────────── */
const TREND = [12, 18, 15, 22, 19, 28, 34, 26, 31, 29, 38, 41, 35, 44]
    .map((v, i) => ({ d: `S${i + 1}`, v }));
const BY_AISLE = [
    { k: 'produce', n: 'Produce', v: 1240 }, { k: 'dairy', n: 'Dairy', v: 980 },
    { k: 'meat', n: 'Meat', v: 760 }, { k: 'bakery', n: 'Bakery', v: 540 },
    { k: 'fish', n: 'Fish', v: 410 }, { k: 'pantry', n: 'Pantry', v: 380 },
    { k: 'other', n: 'Other (3)', v: 290 },
];
const FINDINGS = [
    { en: 'Thursdays 7–9 pm · milk · H3A — 41% of milk adds, against 18% citywide',
      fr: 'Jeudis 19 h–21 h · lait · H3A — 41% des ajouts de lait, contre 18% dans la ville',
      base: 214, hh: 46, idx: 228, tone: 'up' },
    { en: 'Maple Court buys 34% more fish than the city average',
      fr: 'Maple Court achète 34% plus de poisson que la moyenne de la ville',
      base: 311, hh: 46, idx: 134, tone: 'up' },
    { en: 'Sunday 9–11 pm is the busiest time for adding to lists',
      fr: 'Dimanche 21 h–23 h est le moment le plus occupé pour ajouter à la liste',
      base: 1102, hh: 180, idx: 162, tone: 'up' },
    { en: 'Rue Clark buys 18% less detergent than similar buildings',
      fr: 'Rue Clark achète 18% moins de détergent que des immeubles semblables',
      base: 96, hh: 22, idx: 82, tone: 'down' },
];
const CUSTOMERS = [
    { card: 'SOBE001', name: 'Marie T.', phone: '··· 0123', place: 'Maple Court · H3A 1Y4',
      orders: 14, last: '2 days ago' },
    { card: 'SOBE014', name: 'Jean P.', phone: '··· 7781', place: 'Rue Clark · H2X 2J4',
      orders: 9, last: '6 days ago' },
    { card: 'SOBE027', name: 'Ana R.', phone: '··· 3340', place: 'Plateau Lofts · H2T 1W1',
      orders: 21, last: 'yesterday' },
];
const PLACES = [
    { n: 'Maple Court', hh: 46, o: 311, top: 'Fish +34%', low: 'Detergent −18%', idx: 134 },
    { n: 'Plateau Lofts', hh: 38, o: 266, top: 'Produce +28%', low: 'Pork −24%', idx: 128 },
    { n: 'Rue Clark', hh: 22, o: 96, top: 'Detergent +31%', low: 'Fish −22%', idx: 131 },
    { n: 'H2X (postal code)', hh: 71, o: 402, top: 'Bakery +12%', low: 'Fish −9%', idx: 112 },
];
const INDEX_BARS = [
    { n: 'Fish', v: 134 }, { n: 'Beef', v: 122 }, { n: 'Produce', v: 108 },
    { n: 'Bakery', v: 99 }, { n: 'Pork', v: 86 }, { n: 'Detergent', v: 82 },
];
const HEAT_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const HEAT = HEAT_DAYS.map((d, r) => Array.from({ length: 6 }, (_, c) =>
    (r === 3 && c === 4 ? 41 : r === 6 && c === 5 ? 38 : ((r * 7 + c * 5) % 17) + 2)));

const Sample = () => (
    <Badge variant="outline" className="border-amber-500 text-amber-600 dark:text-amber-400"
        data-testid="insights-sample-badge">SAMPLE</Badge>
);

const Kpi = ({ icon: Icon, label, value, note, tone }) => (
    <Card className="p-4 space-y-1" data-testid={`wf-kpi-${label}`}>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Icon className="w-4 h-4" />{label}
        </div>
        <div className={`text-2xl font-semibold ${tone === 'bad'
            ? 'text-rose-600 dark:text-rose-400' : 'text-foreground'}`}>{value}</div>
        {note ? <div className="text-xs text-muted-foreground">{note}</div> : null}
    </Card>
);

const Panel = ({ icon: Icon, title, right, children, testid }) => (
    <Card data-testid={testid}>
        <CardContent className="pt-4 space-y-3">
            <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-sm font-semibold">
                    {Icon ? <Icon className="w-4 h-4 text-emerald-500" /> : null}{title}
                </div>
                {right}
            </div>
            {children}
        </CardContent>
    </Card>
);

const FakeSelect = ({ label }) => (
    <div className="flex h-10 min-w-[9rem] items-center justify-between gap-2 rounded-md border border-border bg-background px-3 text-sm text-muted-foreground">
        {label}<ChevronRight className="h-4 w-4 rotate-90" />
    </div>
);

const FilterBar = ({ t, chips }) => (
    <div className="flex flex-wrap items-center gap-2" data-testid="wf-filter-bar">
        <div className="flex h-10 min-w-[12rem] flex-1 items-center gap-2 rounded-md border border-border bg-background px-3 text-sm text-muted-foreground">
            <Search className="h-4 w-4" />{t.searchPh}
        </div>
        {chips.map((c) => <FakeSelect key={c} label={c} />)}
        <Button variant="outline" size="sm" className="gap-1 min-h-[40px]">
            <Filter className="h-4 w-4" />{t.more}
        </Button>
        <Button variant="ghost" size="sm" className="gap-1 min-h-[40px]">
            <X className="h-4 w-4" />{t.clear}
        </Button>
    </div>
);

const FindingRow = ({ f, lang, t }) => (
    <div className="flex items-start gap-3 rounded-lg border border-border p-3"
        data-testid="wf-finding">
        <span className={`mt-0.5 shrink-0 rounded-full p-1 ${f.tone === 'up'
            ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
            : 'bg-amber-500/15 text-amber-600 dark:text-amber-400'}`}>
            <TrendingUp className={`h-4 w-4 ${f.tone === 'down' ? 'rotate-180' : ''}`} />
        </span>
        <div className="min-w-0">
            <p className="text-sm">{f[lang]}</p>
            <p className="mt-1 text-xs text-muted-foreground">
                {t.base} {f.base} {t.orders} · {f.hh} {t.households} ·
                <span className="font-semibold"> index {f.idx}</span>
            </p>
        </div>
    </div>
);

export default function InsightsWireframes() {
    const { isSuperAdmin } = useAuth();
    const [lang, setLang] = useState('en');
    const [screen, setScreen] = useState('home');
    const [tab, setTab] = useState(0);
    const [dark, setDark] = useState(
        typeof document !== 'undefined' && document.documentElement.classList.contains('dark'));
    const t = T[lang];

    const toggleDark = () => {
        const next = !dark;
        setDark(next);
        document.documentElement.classList.toggle('dark', next);
    };

    const NAV = [
        ['home', Home], ['lookup', Users], ['profile', ShoppingBasket],
        ['explore', Compass], ['places', MapPin], ['patterns', Sparkles], ['summary', FileText],
    ];

    // SUPER ADMIN ONLY, at the page and not only in the menu — typing the URL
    // is not a way in for anybody else, in any vertical.
    if (!isSuperAdmin()) {
        return (
            <div className="p-10 text-sm text-muted-foreground" data-testid="insights-wireframes-denied">
                Not available.
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background text-foreground" data-testid="insights-wireframes">
            {/* the throwaway banner, so nobody mistakes this for the product */}
            <div className="bg-amber-500/15 px-4 py-2 text-center text-xs font-bold uppercase tracking-wider text-amber-700 dark:text-amber-300"
                data-testid="wf-banner">{t.wire}</div>

            <header className="border-b border-border bg-card px-4 py-3">
                <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-2">
                    <Button variant="ghost" size="sm" className="gap-1">
                        <ChevronRight className="h-4 w-4 rotate-180" />{t.back}
                    </Button>
                    <h1 className="text-base font-semibold md:text-lg">{t.title}</h1>
                    <Sample />
                    <div className="ml-auto flex items-center gap-2">
                        <FakeSelect label={t.period} />
                        <Button variant="outline" size="sm" onClick={() => setLang(lang === 'en' ? 'fr' : 'en')}
                            data-testid="wf-lang">{lang === 'en' ? 'FR' : 'EN'}</Button>
                        <Button variant="outline" size="sm" onClick={toggleDark} data-testid="wf-theme">
                            {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                        </Button>
                    </div>
                </div>
                <nav className="mx-auto mt-2 flex max-w-6xl flex-wrap gap-1 pb-1">
                    {NAV.map(([k, Icon]) => (
                        <button key={k} onClick={() => setScreen(k)} data-testid={`wf-nav-${k}`}
                            className={`flex min-h-[44px] shrink-0 items-center gap-1.5 rounded-full px-3 text-sm ${
                                screen === k ? 'bg-emerald-600 text-white'
                                    : 'text-muted-foreground hover:bg-muted'}`}>
                            <Icon className="h-4 w-4" />{t.nav[k]}
                        </button>
                    ))}
                </nav>
            </header>

            <main className="mx-auto max-w-6xl space-y-4 p-4 pb-24">
                {screen === 'home' && (
                    <>
                        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                            <Kpi icon={ShoppingBasket} label={t.kpi.orders} value="1,284" />
                            <Kpi icon={Users} label={t.kpi.households} value="412" />
                            <Kpi icon={AlertTriangle} label={t.kpi.late} value="37" tone="bad" />
                            <Kpi icon={TrendingUp} label={t.kpi.aisle} value="Produce"
                                note={`19% ${t.units}`} />
                        </div>
                        <p className="text-xs text-muted-foreground">{t.noPrices}</p>
                        <div className="grid gap-3 lg:grid-cols-2">
                            <Panel icon={TrendingUp} title={t.trend} right={<Sample />} testid="wf-trend">
                                <div className="h-[200px]">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <LineChart data={TREND}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#33415533" />
                                            <XAxis dataKey="d" tick={{ fontSize: 11 }} />
                                            <YAxis tick={{ fontSize: 11 }} />
                                            <Tooltip />
                                            <Line type="monotone" dataKey="v" stroke={C.emerald}
                                                strokeWidth={2} dot={{ r: 3 }} />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            </Panel>
                            <Panel icon={PieIcon} title={t.share} right={<Sample />} testid="wf-donut">
                                <div className="flex flex-col items-center gap-3 sm:flex-row">
                                    <div className="h-[190px] w-full sm:w-1/2">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <Pie data={BY_AISLE} dataKey="v" nameKey="n"
                                                    innerRadius={45} outerRadius={75} paddingAngle={2}>
                                                    {BY_AISLE.map((a) => <Cell key={a.k} fill={AISLE[a.k]} />)}
                                                </Pie>
                                                <Tooltip />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </div>
                                    <ul className="w-full space-y-1 sm:w-1/2">
                                        {BY_AISLE.map((a) => (
                                            <li key={a.k} className="flex items-center gap-2 text-sm">
                                                <span className="h-2.5 w-2.5 rounded-full"
                                                    style={{ background: AISLE[a.k] }} />
                                                <span className="flex-1">{a.n}</span>
                                                <span className="font-semibold tabular-nums">{a.v}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </Panel>
                        </div>
                        <Panel icon={Sparkles} title={t.noticed}
                            right={<Button variant="ghost" size="sm" onClick={() => setScreen('patterns')}>
                                {t.seeAll}</Button>} testid="wf-home-findings">
                            <div className="space-y-2">
                                {FINDINGS.slice(0, 3).map((f, i) => <FindingRow key={i} f={f} lang={lang} t={t} />)}
                            </div>
                        </Panel>
                    </>
                )}

                {screen === 'lookup' && (
                    <>
                        <FilterBar t={t} chips={['Building', 'Status', 'Language']} />
                        <Panel icon={Lock} title={t.reason} testid="wf-reason">
                            <div className="flex h-10 items-center rounded-md border border-border bg-background px-3 text-sm text-muted-foreground">
                                {t.reasonPh}
                            </div>
                        </Panel>
                        <Panel icon={Users} title={`3 ${t.results}`} right={<Sample />} testid="wf-results">
                            <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Card</TableHead><TableHead>Name</TableHead>
                                            <TableHead>Phone</TableHead><TableHead>Place</TableHead>
                                            <TableHead>Orders</TableHead><TableHead>Last</TableHead>
                                            <TableHead></TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {CUSTOMERS.map((c) => (
                                            <TableRow key={c.card}>
                                                <TableCell><Badge variant="secondary">{c.card}</Badge></TableCell>
                                                <TableCell className="font-medium">{c.name}</TableCell>
                                                <TableCell className="text-muted-foreground">{c.phone}
                                                    <span className="ml-1 text-xs">({t.masked})</span></TableCell>
                                                <TableCell>{c.place}</TableCell>
                                                <TableCell className="tabular-nums">{c.orders}</TableCell>
                                                <TableCell className="text-muted-foreground">{c.last}</TableCell>
                                                <TableCell>
                                                    <Button size="sm" variant="outline"
                                                        onClick={() => setScreen('profile')}>{t.open}</Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </Panel>
                    </>
                )}

                {screen === 'profile' && (
                    <>
                        <Card><CardContent className="flex flex-wrap items-center gap-3 pt-4">
                            <Badge variant="secondary" className="text-base">SOBE001</Badge>
                            <div className="min-w-0">
                                <p className="font-semibold">Marie T.</p>
                                <p className="text-xs text-muted-foreground">
                                    Maple Court · H3A 1Y4 · EN · member since Mar 2026 · 2 members</p>
                            </div>
                            <Sample />
                        </CardContent></Card>
                        {/* A scroller with no edge looks like a cut-off word, so the
                            nine tabs get the same fade the grocery switcher uses. */}
                        <div className="relative" data-testid="wf-profile-tabs">
                        <span aria-hidden className="pointer-events-none absolute right-0 top-0 z-10 h-full w-10 bg-gradient-to-l from-background to-transparent" />
                        <div className="flex gap-1 overflow-x-auto pb-1">
                            {t.tabs.map((name, i) => (
                                <button key={i} onClick={() => setTab(i)} data-testid={`wf-tab-${i}`}
                                    className={`min-h-[44px] shrink-0 rounded-full px-3 text-sm ${
                                        tab === i ? 'bg-emerald-600 text-white'
                                            : 'text-muted-foreground hover:bg-muted'}`}>{name}</button>
                            ))}
                        </div>
                        </div>
                        <Panel title={t.tabs[tab]} right={<Sample />} testid="wf-tab-body">
                            {tab === 3 ? (
                                <>
                                    <p className="text-xs text-muted-foreground">{t.noPrices}</p>
                                    <div className="h-[180px]">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart data={BY_AISLE}>
                                                <CartesianGrid strokeDasharray="3 3" stroke="#33415533" />
                                                <XAxis dataKey="n" tick={{ fontSize: 10 }} />
                                                <YAxis tick={{ fontSize: 11 }} />
                                                <Tooltip />
                                                <Bar dataKey="v" radius={[4, 4, 0, 0]}>
                                                    {BY_AISLE.map((a) => <Cell key={a.k} fill={AISLE[a.k]} />)}
                                                </Bar>
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                </>
                            ) : tab === 8 ? (
                                <ul className="space-y-2 text-sm">
                                    {[['Jay C. · store manager', 'Support call about a late order', 'today 09:12'],
                                      ['Ana L. · analyst', 'Checked a delivery complaint', 'Sep 28 14:40']]
                                        .map(([who, why, when]) => (
                                            <li key={when} className="rounded-lg border border-border p-3">
                                                <p className="font-medium">{who}</p>
                                                <p className="text-muted-foreground">{why} · {when}</p>
                                            </li>
                                        ))}
                                </ul>
                            ) : (
                                <ul className="space-y-2 text-sm">
                                    {['Milk · 2% · 2 L — every 6 days',
                                      'Sandwich bread — every 8 days',
                                      'Bananas · by weight — every 5 days'].map((x) => (
                                        <li key={x} className="flex items-center gap-2 rounded-lg border border-border p-3">
                                            <Clock className="h-4 w-4 text-muted-foreground" />{x}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </Panel>
                    </>
                )}

                {screen === 'explore' && (
                    <>
                        <FilterBar t={t} chips={['Aisle', 'Place', 'Clock: added', 'Group by']} />
                        <div className="grid gap-3 lg:grid-cols-2">
                            <Panel icon={Clock} title="Day and hour · added to list" right={<Sample />}
                                testid="wf-heatmap">
                                <div className="overflow-x-auto">
                                    <div className="min-w-[250px]">
                                        <div className="mb-1 grid grid-cols-[2.4rem_repeat(6,minmax(0,1fr))] gap-1 text-[10px] text-muted-foreground">
                                            <span />{['6a', '9a', '12p', '3p', '6p', '9p'].map((h) =>
                                                <span key={h} className="text-center">{h}</span>)}
                                        </div>
                                        {HEAT.map((row, r) => (
                                            <div key={r} className="mb-1 grid grid-cols-[2.4rem_repeat(6,minmax(0,1fr))] items-center gap-1">
                                                <span className="text-[11px] text-muted-foreground">{HEAT_DAYS[r]}</span>
                                                {row.map((v, c) => (
                                                    /* EVERY cell prints its number. A ramp alone
                                                       is unreadable for a colour-blind grocer, and
                                                       the first draft of this only printed the
                                                       darkest cell — which hid the comparison. */
                                                    <span key={c} title={`${v}`}
                                                        className="flex h-8 items-center justify-center rounded text-[10px] font-semibold tabular-nums"
                                                        style={{ background: `rgba(16,185,129,${0.08 + (v / 41) * 0.85})`,
                                                            color: v / 41 > 0.55 ? '#06281f' : 'inherit' }}>
                                                        {v}
                                                    </span>
                                                ))}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </Panel>
                            <Panel icon={Compass} title="Dairy vs Bakery · this month vs last"
                                right={<Sample />} testid="wf-compare">
                                <div className="h-[200px]">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={[
                                            { n: 'Dairy', a: 980, b: 870 }, { n: 'Bakery', a: 540, b: 601 },
                                        ]}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#33415533" />
                                            <XAxis dataKey="n" tick={{ fontSize: 11 }} />
                                            <YAxis tick={{ fontSize: 11 }} /><Tooltip />
                                            <Bar dataKey="a" fill={C.blue} radius={[4, 4, 0, 0]} />
                                            <Bar dataKey="b" fill={C.slate} radius={[4, 4, 0, 0]} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                                <p className="text-xs text-muted-foreground">
                                    Dairy +12.6% · Bakery −10.1% · index 113 / 90</p>
                            </Panel>
                        </div>
                        <Panel title="Lines behind these figures"
                            right={<Button variant="outline" size="sm" className="gap-1">
                                <Download className="h-4 w-4" />{t.export}</Button>} testid="wf-explore-table">
                            <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader><TableRow>
                                        <TableHead>Item</TableHead><TableHead>Aisle</TableHead>
                                        <TableHead>Units</TableHead><TableHead>Households</TableHead>
                                        <TableHead>Index</TableHead>
                                    </TableRow></TableHeader>
                                    <TableBody>
                                        {[['Milk', 'dairy', 412, 96, 128], ['Bananas', 'produce', 388, 104, 112],
                                          ['Salmon', 'fish', 96, 41, 134], ['Detergent', 'cleaning', 71, 38, 82]]
                                            .map(([i, a, u, h, x]) => (
                                                <TableRow key={i}>
                                                    <TableCell className="font-medium">{i}</TableCell>
                                                    <TableCell><Badge variant="outline"
                                                        style={{ borderColor: AISLE[a], color: AISLE[a] }}>{a}</Badge></TableCell>
                                                    <TableCell className="tabular-nums">{u}</TableCell>
                                                    <TableCell className="tabular-nums">{h}</TableCell>
                                                    <TableCell><Badge variant={x >= 130 ? 'default' : 'secondary'}>{x}</Badge></TableCell>
                                                </TableRow>
                                            ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </Panel>
                    </>
                )}

                {screen === 'places' && (
                    <>
                        <FilterBar t={t} chips={['City', 'Postal code', 'Building']} />
                        <Panel icon={Building2} title={t.placesTitle} right={<Sample />} testid="wf-places">
                            <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader><TableRow>
                                        <TableHead>Place</TableHead><TableHead>{t.households}</TableHead>
                                        <TableHead>{t.orders}</TableHead><TableHead>Over-indexes</TableHead>
                                        <TableHead>Under-indexes</TableHead>
                                    </TableRow></TableHeader>
                                    <TableBody>
                                        {PLACES.map((p) => (
                                            <TableRow key={p.n}>
                                                <TableCell className="font-medium">{p.n}</TableCell>
                                                <TableCell className="tabular-nums">{p.hh}</TableCell>
                                                <TableCell className="tabular-nums">{p.o}</TableCell>
                                                <TableCell><Badge className="bg-emerald-600">{p.top}</Badge></TableCell>
                                                <TableCell><Badge variant="outline"
                                                    className="border-amber-500 text-amber-600 dark:text-amber-400">{p.low}</Badge></TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </Panel>
                        <Panel icon={MapPin} title={`Maple Court · ${t.index}`} right={<Sample />}
                            testid="wf-index-bars">
                            <div className="space-y-2">
                                {INDEX_BARS.map((b) => {
                                    const over = b.v >= 100;
                                    const w = Math.min(50, Math.abs(b.v - 100));
                                    return (
                                        <div key={b.n} className="flex items-center gap-2">
                                            <span className="w-24 shrink-0 text-sm">{b.n}</span>
                                            <div className="relative h-6 flex-1 rounded bg-muted">
                                                <span className="absolute left-1/2 top-0 h-full w-px bg-border" />
                                                <span className="absolute top-0 h-full rounded"
                                                    style={{ background: over ? C.emerald : C.amber,
                                                        left: over ? '50%' : `${50 - w}%`, width: `${w}%` }} />
                                            </div>
                                            <span className={`w-16 shrink-0 text-right text-sm font-semibold tabular-nums ${
                                                over ? 'text-emerald-600 dark:text-emerald-400'
                                                    : 'text-amber-600 dark:text-amber-400'}`}>
                                                {over ? '+' : '−'}{Math.abs(b.v - 100)}%
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                            <p className="text-xs text-muted-foreground">
                                {t.city} = 100 · {t.base} 311 {t.orders}, 46 {t.households}</p>
                        </Panel>
                    </>
                )}

                {screen === 'patterns' && (
                    <Panel icon={Sparkles} title={t.noticed} right={<Sample />} testid="wf-patterns">
                        <div className="space-y-2">
                            {FINDINGS.map((f, i) => <FindingRow key={i} f={f} lang={lang} t={t} />)}
                        </div>
                        <p className="text-xs text-muted-foreground">
                            Hidden below 30 events or 5 households · 20 households for certified
                            products · false-discovery control at q = 0.05 · must hold in 2 of the
                            last 3 periods</p>
                    </Panel>
                )}

                {screen === 'summary' && (
                    <>
                        <div className="flex flex-wrap items-center gap-2">
                            <FakeSelect label={t.scope} />
                            <FakeSelect label={t.period} />
                            <Button size="sm" className="gap-1 min-h-[40px]">
                                <Download className="h-4 w-4" />{t.pdf}</Button>
                            <Button variant="outline" size="sm" className="min-h-[40px]">{t.weekly}</Button>
                            <Sample />
                        </div>
                        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                            <Kpi icon={ShoppingBasket} label={t.kpi.orders} value="1,284" note="+8% vs last" />
                            <Kpi icon={Users} label={t.kpi.households} value="412" note="+14" />
                            <Kpi icon={AlertTriangle} label={t.kpi.late} value="37" tone="bad" note="2.9%" />
                            <Kpi icon={TrendingUp} label={t.kpi.aisle} value="Produce" note={`19% ${t.units}`} />
                        </div>
                        <Panel title={t.findings} testid="wf-summary-findings">
                            <div className="space-y-2">
                                {FINDINGS.slice(0, 3).map((f, i) => <FindingRow key={i} f={f} lang={lang} t={t} />)}
                            </div>
                        </Panel>
                        <div className="grid gap-3 lg:grid-cols-2">
                            <Panel title={t.opps} testid="wf-summary-opps">
                                <ul className="space-y-2 text-sm">
                                    <li className="rounded-lg border border-border p-3">
                                        Rue Clark buys 22% less fish than similar buildings — 22 households</li>
                                    <li className="rounded-lg border border-border p-3">
                                        Thursday 7–9 pm windows fill first in H3A — add capacity</li>
                                </ul>
                            </Panel>
                            <Panel title={t.actions} testid="wf-summary-actions">
                                <ul className="space-y-2 text-sm">
                                    <li className="rounded-lg border border-border p-3">
                                        Stock more salmon for Maple Court deliveries</li>
                                    <li className="rounded-lg border border-border p-3">
                                        Open one more Thursday evening window in H3A</li>
                                </ul>
                            </Panel>
                        </div>
                        <p className="text-xs text-muted-foreground">{t.computed}</p>
                    </>
                )}
            </main>
        </div>
    );
}
